/**
 * Report Template Service — Firestore source of truth, localStorage as cache.
 *
 * Selector list shown in the UI = user templates ∪ user's company templates.
 * Each template carries a `scope` tag derived from the Firestore path.
 *
 * Paths:
 *   per-user:    users/{uid}/reportTemplates/{templateId}
 *   per-company: companies/{cid}/reportTemplates/{templateId}
 */
import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { ReportTemplate } from '../types/reportTemplate';
import { stripUndefined } from '../lib/firestore/stripUndefined';
import { diffFieldPaths, pushVersionEntry, stripHistory } from '../lib/templateDiff';
import {
  coerceCustomSectionEntries,
  coerceSectionConfigList,
  normalizeHeadingOverrides,
} from '../lib/reportTemplateNormalization';
import { normalizeCoverLayout } from '../lib/normalizeCoverLayout';

/** TPP-5 — versionHistory cap. Mirrors the constant pinned in
 *  templateDiff.ts (`HISTORY_CAP = 10`); duplicated here as a service-
 *  level invariant so the no-op guard + ring trimming live together. */
const HISTORY_CAP = 10;

export const TEMPLATES_LOCAL_KEY = 'deepatomic_report_templates';

const userTemplatesCol = (uid: string) => collection(db, `users/${uid}/reportTemplates`);
const companyTemplatesCol = (cid: string) => collection(db, `companies/${cid}/reportTemplates`);
const userTemplateDoc = (uid: string, templateId: string) => doc(db, `users/${uid}/reportTemplates`, templateId);
const companyTemplateDoc = (cid: string, templateId: string) => doc(db, `companies/${cid}/reportTemplates`, templateId);

function sanitizeSectionsByReportType(
  raw: ReportTemplate['sectionsByReportType'],
): ReportTemplate['sectionsByReportType'] {
  if (!raw || typeof raw !== 'object') return raw;
  return Object.fromEntries(
    Object.entries(raw).flatMap(([type, sections]) => {
      if (sections === undefined) return [];
      return [[type, coerceSectionConfigList(sections)]];
    }),
  ) as ReportTemplate['sectionsByReportType'];
}

/** Lightweight fetch-time sanitize — coerces corrupt shapes without mutating semantics (no toc inject). */
function normalizeTemplate(template: ReportTemplate, scope: 'user' | 'company'): ReportTemplate {
  const coercedSections = coerceSectionConfigList(template.sections);
  return {
    ...template,
    scope,
    // STAR-DEFAULT-PERSIST FIX (#7, secondary) — `isDefault` is a PER-USER
    // preference. `setDefaultTemplate` only ever write-throughs the flag on
    // user-scoped docs (company docs are shared, so a per-user default must not
    // be stamped on them). A company doc may still carry a STALE isDefault:true
    // (e.g. left over from a prior data shape or a template later promoted to
    // company scope). Force it false on read so a stale company default can
    // never win in resolvePreferredTemplate — avoiding two-default ambiguity.
    ...(scope === 'company' ? { isDefault: false } : {}),
    headingOverrides: normalizeHeadingOverrides(template.headingOverrides),
    coverLayout: normalizeCoverLayout(template.coverLayout),
    ...(coercedSections.length > 0 ? { sections: coercedSections } : {}),
    ...(template.sectionsByReportType
      ? { sectionsByReportType: sanitizeSectionsByReportType(template.sectionsByReportType) }
      : {}),
    ...(template.customSections != null
      ? { customSections: coerceCustomSectionEntries(template.customSections) }
      : {}),
  };
}

/**
 * Fetch the union of user-scoped and company-scoped templates. Each template
 * is tagged with its scope (overriding any stored value to match the path it
 * was loaded from). Errors at either tier are logged and skipped.
 */
export async function fetchReportTemplates(
  uid: string,
  companyId?: string | null,
): Promise<ReportTemplate[]> {
  const templates: ReportTemplate[] = [];

  try {
    const userSnap = await getDocs(userTemplatesCol(uid));
    for (const d of userSnap.docs) {
      templates.push(normalizeTemplate({ ...(d.data() as ReportTemplate), id: d.id }, 'user'));
    }
  } catch (e) {
    console.warn('reportTemplateService: user fetch failed', e);
  }

  if (companyId) {
    try {
      const companySnap = await getDocs(companyTemplatesCol(companyId));
      for (const d of companySnap.docs) {
        templates.push(normalizeTemplate({ ...(d.data() as ReportTemplate), id: d.id }, 'company'));
      }
    } catch (e) {
      console.warn('reportTemplateService: company fetch failed', e);
    }
  }

  return templates;
}

/**
 * Fetch ONLY the company-scoped templates for a given company.
 *
 * Used for cross-org report viewing: when a viewer opens a report created by a
 * DIFFERENT company (always a descendant org, since report visibility is
 * downward-only in the company tree), the report must render with the template
 * assigned by the CREATING company — not the viewer's own. The viewer cannot
 * read the creator's user-scoped (`users/{uid}`) templates, but Firestore rules
 * (`isInCompanyTree(companyId)`) permit an ancestor to read a descendant's
 * `companies/{companyId}/reportTemplates`, which is exactly the assigned-org
 * template the requirement targets. A permission-denied (non-ancestor) fetch is
 * logged and returns [] so resolution falls back to the report's archetype seed.
 */
export async function fetchCompanyReportTemplates(
  companyId: string,
): Promise<ReportTemplate[]> {
  const templates: ReportTemplate[] = [];
  try {
    const companySnap = await getDocs(companyTemplatesCol(companyId));
    for (const d of companySnap.docs) {
      templates.push(normalizeTemplate({ ...(d.data() as ReportTemplate), id: d.id }, 'company'));
    }
  } catch (e) {
    console.warn('reportTemplateService: cross-org company fetch failed', e);
  }
  return templates;
}

/**
 * Persist a single template. The scope determines which Firestore path is used.
 * Company-scoped writes are gated by Firestore rules — non-admin members will
 * see a permission-denied error which the caller should surface.
 */
export async function saveReportTemplate(
  ownerId: string,
  template: ReportTemplate,
  scope: 'user' | 'company',
): Promise<void> {
  const ref = scope === 'company'
    ? companyTemplateDoc(ownerId, template.id)
    : userTemplateDoc(ownerId, template.id);
  // CDS-1 — scrub undefined fields before the Firestore write. The
  // RTU-4 `sectionsByReportType` is sparse (only edited types exist as
  // keys), so an unset type-id key arrives as `undefined` if the
  // caller spreads partial overrides. Firestore's web SDK is
  // configured WITHOUT `ignoreUndefinedProperties` (deliberate, see
  // src/firebase.ts), which would otherwise reject the payload.
  const normalized = normalizeTemplate(template, scope);
  const payload = stripUndefined(normalized);
  // Full replace (NOT { merge: true }). The sparse maps (sectionsByReportType,
  // sectionVisibility, sectionColumnLayouts, headingOverrides) support KEY
  // DELETION: when the user removes a per-type override, that key arrives absent
  // and stripUndefined drops it. A Firestore merge only SETS present keys and
  // never DELETES absent ones, so a merge would leave the old value on the doc
  // and it would reappear on the next load. A full replace is safe because
  // normalizeTemplate fills every required default and preserves createdAt /
  // updatedAt (it spreads ...template), so no field is lost.
  await setDoc(ref, payload);
}

/**
 * TPP-5 (2026-05-19, row 0.99296) — pure helper that returns the
 * next template snapshot to persist with correct version + history
 * semantics.
 *
 * Pre-fix the Studio version chip stayed at `v2` across multiple
 * edit + save cycles because the store's `updateReportTemplate`
 * unconditionally bumped `version` (which `pushVersionEntry` then
 * snapshotted at `version === 2` over and over). The user saw the
 * same chip after every Save and assumed nothing had been
 * recorded. This helper makes version bumps observable:
 *
 *   1. Compute the dot-path diff between the prior snapshot and the
 *      next snapshot (both stripped of versionHistory so the ring
 *      structure isn't itself a diff source).
 *   2. If the diff is empty → no-op save. Returns the next template
 *      verbatim with its version + versionHistory unchanged. The
 *      caller writes it through anyway so `updatedAt` refreshes,
 *      but the chip won't move.
 *   3. If the diff is non-empty → bump `version = (prior.version ?? 1)
 *      + 1` and push the PRIOR snapshot onto `versionHistory[]` (cap
 *      `HISTORY_CAP = 10`, newest first). Honours the same
 *      "stripped snapshot" rule as `pushVersionEntry` so a v100
 *      template doesn't carry 10 recursive copies of itself.
 *
 * `prior === null` means the template doesn't exist yet (first save)
 * — version starts at 1, history stays empty.
 *
 * If the caller explicitly sets `next.version` (test fixtures,
 * forced-bump UX, restore-from-history) we honour that exact value
 * instead of computing a bump. The history push is still gated on a
 * non-empty diff so a restore that produces no net change doesn't
 * grow the ring.
 *
 * Pure function — no Firestore, no I/O. Tested under
 * `reportTemplateService.versionBump.test.ts`.
 */
export function prepareTemplateSaveWithVersionBump(
  prior: ReportTemplate | null,
  next: ReportTemplate,
  savedBy: string = 'anonymous',
  savedAt: string = new Date().toISOString(),
  savedByEmail?: string,
): ReportTemplate {
  if (!prior) {
    // First save — start at version 1 (or honour caller-provided),
    // empty history ring.
    const callerVersion = typeof next.version === 'number' && Number.isFinite(next.version)
      ? next.version
      : 1;
    return { ...next, version: callerVersion, versionHistory: next.versionHistory ?? [] };
  }

  // Diff is computed on the STRIPPED snapshots so a previously-grown
  // versionHistory doesn't show up as a self-diff. The diff helper
  // already excludes `versionHistory` + `updatedAt` + `createdAt`
  // from its default set, but stripHistory belt-and-braces it.
  const beforeSnap = stripHistory(prior) as unknown as Record<string, unknown>;
  const afterSnap = stripHistory(next) as unknown as Record<string, unknown>;
  const fieldsChanged = diffFieldPaths(beforeSnap, afterSnap);

  if (fieldsChanged.length === 0) {
    // No-op save. Keep the prior version + history; pass everything
    // else through (the caller may still want to refresh `updatedAt`
    // on the Firestore doc, which the store / studio sets before
    // calling us).
    return {
      ...next,
      version: prior.version ?? 1,
      versionHistory: prior.versionHistory ?? next.versionHistory ?? [],
    };
  }

  // Real diff. Bump version and push the prior snapshot.
  const priorVersion = typeof prior.version === 'number' && Number.isFinite(prior.version)
    ? prior.version
    : 1;
  // Only honour `next.version` when the caller explicitly chose a
  // value distinct from `prior.version`. A spread like `next = { ...prior, name: 'x' }`
  // carries `prior.version` through unchanged — treating that as
  // "caller explicitly set v1" would freeze the chip at v1 forever.
  // Restore-from-history flows set `next.version` to a value LOWER
  // than prior (e.g. restoring v3 over v5) — that case is honoured.
  const callerVersionExplicit =
    typeof next.version === 'number' && Number.isFinite(next.version) && next.version !== priorVersion;
  const nextVersion = callerVersionExplicit ? next.version! : priorVersion + 1;
  const nextHistory = pushVersionEntry(prior, savedBy, savedAt, savedByEmail).slice(0, HISTORY_CAP);
  return { ...next, version: nextVersion, versionHistory: nextHistory };
}

/** Delete a single template by id within the given scope. */
export async function deleteReportTemplateDoc(
  ownerId: string,
  templateId: string,
  scope: 'user' | 'company',
): Promise<void> {
  const ref = scope === 'company'
    ? companyTemplateDoc(ownerId, templateId)
    : userTemplateDoc(ownerId, templateId);
  await deleteDoc(ref);
}

/** Read the localStorage cache (offline fallback). Backfills scope on legacy entries. */
export function readCachedReportTemplates(): ReportTemplate[] | null {
  try {
    const saved = localStorage.getItem(TEMPLATES_LOCAL_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Array<Partial<ReportTemplate> & { id: string }>;
      return parsed.map(t => normalizeTemplate({ ...t, scope: t.scope ?? 'user' } as ReportTemplate, t.scope ?? 'user'));
    }
  } catch { /* ignore */ }
  return null;
}

/** Write the localStorage cache (called by the store after in-memory updates). */
export function writeCachedReportTemplates(templates: ReportTemplate[]): void {
  try { localStorage.setItem(TEMPLATES_LOCAL_KEY, JSON.stringify(templates.map(t => normalizeTemplate(t, t.scope ?? 'user')))); }
  catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
}
